import { useState } from 'react';
import {
  Alert,
  Box,
  CloseButton,
  Heading,
  Icon,
  Paragraph,
  Popover,
  Switch,
  Table,
  Text,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconErrorFilled, IconInfoFilled } from '@zillow/constellation-icons';
import {
  ApplicationsTermsOfUse,
  applicationUtils,
  DatarouterError,
  MAX_NUMBER_OF_APPLICATION_PERMISSIONS,
  PERMISSION_STATUS,
  RentalManagerUserManagementApplicationPermissionWebApiHandler_dismissPermissionRequests,
  RentalManagerUserManagementApplicationPermissionWebApiHandler_giveUserApplicationPermission,
  RentalManagerUserManagementApplicationPermissionWebApiHandler_removeUserApplicationPermission,
  stringHelper,
  USER_MANAGEMENT_ROLE,
  useRentalManagerUser,
  userTableHeaders,
} from '@zg-rentals/rental-manager-env';
import {
  applicationPermissionsTableStyles,
  applicationUsersContainerStyles,
  applicationUsersTableContainerStyles,
  errorContainerStyles,
  errorStyles,
  infoIconContainerStyles,
  infoIconStyles,
  userInitialStyles,
} from './StyledComponents';
import { useApplicationPermissions } from '../../hooks/useApplicationPermissions';

// @ts-ignore
const ApplicationUsers = () => {
  const [error, setError] = useState('');
  const { user } = useRentalManagerUser();
  const { userManagementCompanyId, userToken: currentUserToken } = user;
  const { getUsersWithApplicationPermissionDetails, applicationManager, pendingApplicationRequestUsers, users } =
    useApplicationPermissions();
  const applicationUsers = users.filter((u) => u.userManagementRoleString !== USER_MANAGEMENT_ROLE.applicationManager);
  const handleCloseButtonClick = () => {
    RentalManagerUserManagementApplicationPermissionWebApiHandler_dismissPermissionRequests({
      userManagementCompanyId,
    });
  };

  const { enqueueToast } = useToast();

  const setUserApplicationPermissions = (
    userManagementCompanyId: string,
    userToken: string,
    permissionStatus: string,
  ) => {
    let endpoint;
    endpoint = RentalManagerUserManagementApplicationPermissionWebApiHandler_giveUserApplicationPermission;
    if (permissionStatus === PERMISSION_STATUS.granted) {
      endpoint = RentalManagerUserManagementApplicationPermissionWebApiHandler_removeUserApplicationPermission;
    }
    return endpoint({ userToken, userManagementCompanyId });
  };

  // @ts-ignore
  const onPermissionChange = (user) => {
    const { userToken, permissionStatus } = user;
    const userPermissionGrantedCount = applicationUsers.filter(
      (u) => u.permissionStatus === PERMISSION_STATUS.granted,
    ).length;
    const isGivingPermission = permissionStatus !== PERMISSION_STATUS.granted;
    const hasMaxNumberOfApplicationUsers = userPermissionGrantedCount >= MAX_NUMBER_OF_APPLICATION_PERMISSIONS;
    if (error) {
      setError('');
    }
    if (hasMaxNumberOfApplicationUsers && isGivingPermission) {
      setError('The maximum number of users already have application permissions assigned.');
    } else {
      setUserApplicationPermissions(userManagementCompanyId, userToken, permissionStatus)
        .then(getUsersWithApplicationPermissionDetails)
        .then((results) => {
          const applicationUsersCount =
            results?.filter((u) => u.permissionStatus === PERMISSION_STATUS.granted).length || 0;
          const seatLeftCount = MAX_NUMBER_OF_APPLICATION_PERMISSIONS - applicationUsersCount;
          const actionText = isGivingPermission ? 'gave' : 'removed';
          const seatText =
            seatLeftCount === 0
              ? 'No seat left'
              : `${stringHelper.pluralize(seatLeftCount, 'seat remains', 'seats remain')}`;

          const body = `You ${actionText} ${applicationUtils.getName(user)} application permissions.
                                ${seatText} for your account.
                            `;
          enqueueToast(<Toast tone="success">{body}</Toast>);
          return;
        })
        .catch((error) => {
          setError(DatarouterError.getDisplayMessage(error));
        });
    }
  };

  const isApplicationManager = applicationManager?.userToken === currentUserToken;
  const userNamesRequestingPermission = pendingApplicationRequestUsers.map((user) => applicationUtils.getName(user));

  return (
    // @ts-ignore
    <Box className={applicationUsersContainerStyles}>
      <Text textStyle="heading-xs" asChild>
        <p>Permissions</p>
      </Text>
      <Text asChild>
        <p>
          Up to 5 users on this account can have application permissions, allowing them to view and accept applications.
          The application manager can change user permissions at any time.
          <Popover
            header={<Heading level={6}>Application manager privileges</Heading>}
            body={<ApplicationsTermsOfUse />}
            placement="top"
            trigger={
              // @ts-ignore
              <span className={infoIconContainerStyles}>
                <Icon render={<IconInfoFilled />} className={infoIconStyles} css={{ color: 'blue600' }} />
              </span>
            }
          />
        </p>
      </Text>
      {/* @ts-ignore */}
      <Box className={applicationUsersTableContainerStyles}>
        {userNamesRequestingPermission.length > 0 && (
          <Box css={{ marginBlockEnd: '12px' }}>
            <Alert.Root>
              <Alert.Body>{`${userNamesRequestingPermission.join(', ')} requested application permissions.`}</Alert.Body>
              <Alert.Close>
                <CloseButton onClick={handleCloseButtonClick} />
              </Alert.Close>
            </Alert.Root>
          </Box>
        )}
        {error && (
          <Box className={errorContainerStyles}>
            <Box css={{ display: 'flex', alignItems: 'center' }}>
              <Icon render={<IconErrorFilled />} css={{ color: 'red600' }} size="sm" />
              <Paragraph className={errorStyles} css={{ marginInlineStart: 'sm' }}>
                {error}
              </Paragraph>
            </Box>
          </Box>
        )}
        <Table.Responsive>
          <Table.Root
            className={applicationPermissionsTableStyles}
            appearance="horizontal"
            aria-label="User permission details table"
          >
            <Table.Header>
              <Table.Row>
                {userTableHeaders.map((header) => (
                  <Table.HeaderCell key={header}>{header}</Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {applicationUsers.map((user) => (
                <Table.Row key={user.userToken}>
                  <Table.Cell>
                    <Box css={{ display: 'flex', alignItems: 'center' }}>
                      <Text textStyle="heading-xs" asChild>
                        <p className={userInitialStyles}>{(user.firstName?.[0] || user.userEmail[0]).toUpperCase()}</p>
                      </Text>
                      <div>
                        {(user.firstName || user.lastName) && (
                          <Text textStyle="heading-xs" asChild>
                            <p>{`${user.firstName} ${user.lastName}`}</p>
                          </Text>
                        )}
                        <Text asChild>
                          <p>{user.userEmail}</p>
                        </Text>
                      </div>
                    </Box>
                  </Table.Cell>
                  <Table.Cell>{user.userManagementRoleString}</Table.Cell>
                  <Table.Cell>
                    <Switch
                      checked={user.permissionStatus === PERMISSION_STATUS.granted}
                      onChange={() => onPermissionChange(user)}
                      disabled={!isApplicationManager}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Table.Responsive>
      </Box>
    </Box>
  );
};

export default ApplicationUsers;
